import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Shield, Download, Filter, RefreshCw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RiskBadge({ risk }) {
  const cfg = {
    Low:            "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
    Moderate:       "text-amber-400 bg-amber-400/10 border-amber-400/30",
    "Moderate-High":"text-orange-400 bg-orange-400/10 border-orange-400/30",
    High:           "text-red-400 bg-red-400/10 border-red-400/30",
    "Very High":    "text-red-500 bg-red-500/10 border-red-500/30",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-mono font-semibold border tracking-wider uppercase ${cfg[risk] || cfg.High}`}>
      {risk}
    </span>
  );
}

const RISK_OPTIONS = ["", "Low", "Moderate", "Moderate-High", "High"];
const GOV_OPTIONS  = ["", "Entry", "Level 1", "Level 2", "Level 3"];

export default function Admin() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [csvLoading, setCsvLoading] = useState(false);
  const [filters, setFilters] = useState({ submission_risk: "", governance_level: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/submissions?limit=500`);
      setSubmissions(data.submissions || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return submissions.filter((s) => {
      const res = s.results || {};
      if (filters.submission_risk && res.submission_risk !== filters.submission_risk) return false;
      if (filters.governance_level && res.governance_level !== filters.governance_level) return false;
      return true;
    });
  }, [submissions, filters]);

  const handleExport = async () => {
    setCsvLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/submissions/export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "disp_submissions.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1f33] text-[#f1f5f9] font-['Inter',sans-serif]">
      {/* Header */}
      <div className="bg-[#112d4e] border-b border-[#1e3a5f] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield size={18} className="text-[#c9a84c]" />
            <span className="font-['Manrope',sans-serif] font-bold text-[#c9a84c] tracking-widest uppercase text-sm">
              DefencIQ™ Admin
            </span>
            <span className="text-[#334155] text-sm">/ Submissions</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="refresh-btn"
              onClick={load}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#1e3a5f] rounded-sm text-xs text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#c9a84c]/40 transition-all"
            >
              <RefreshCw size={12} />
              Refresh
            </button>
            <button
              data-testid="export-csv-btn"
              onClick={handleExport}
              disabled={csvLoading}
              className="inline-flex items-center gap-1.5 bg-[#c9a84c] text-[#0a1f33] font-['Manrope',sans-serif] font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-[#d4b460] transition-all disabled:opacity-60"
            >
              {csvLoading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Submissions", value: submissions.length },
            { label: "Shown (filtered)", value: filtered.length },
            { label: "High Risk",  value: submissions.filter(s => s.results?.submission_risk === "High").length },
            { label: "Low Risk",   value: submissions.filter(s => s.results?.submission_risk === "Low").length },
          ].map(({ label, value }, i) => (
            <div key={i} className="bg-[#112d4e] border border-[#1e3a5f] rounded-sm p-4"
              data-testid={`stat-${i}`}>
              <p className="text-[#94a3b8] text-xs uppercase tracking-widest mb-1">{label}</p>
              <p className="font-['JetBrains_Mono',monospace] font-bold text-2xl text-[#f1f5f9]">{value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <Filter size={14} className="text-[#94a3b8]" />
          <select
            data-testid="filter-submission-risk"
            value={filters.submission_risk}
            onChange={(e) => setFilters((p) => ({ ...p, submission_risk: e.target.value }))}
            className="bg-[#112d4e] border border-[#1e3a5f] rounded-sm px-3 py-2 text-sm text-[#f1f5f9] focus:outline-none focus:ring-1 focus:ring-[#c9a84c]"
          >
            <option value="">All Submission Risk</option>
            {RISK_OPTIONS.filter(Boolean).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select
            data-testid="filter-governance-level"
            value={filters.governance_level}
            onChange={(e) => setFilters((p) => ({ ...p, governance_level: e.target.value }))}
            className="bg-[#112d4e] border border-[#1e3a5f] rounded-sm px-3 py-2 text-sm text-[#f1f5f9] focus:outline-none focus:ring-1 focus:ring-[#c9a84c]"
          >
            <option value="">All Governance Levels</option>
            {GOV_OPTIONS.filter(Boolean).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          {(filters.submission_risk || filters.governance_level) && (
            <button
              data-testid="clear-filters-btn"
              onClick={() => setFilters({ submission_risk: "", governance_level: "" })}
              className="text-xs text-[#94a3b8] hover:text-[#c9a84c] transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-[#112d4e] border border-[#1e3a5f] rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="submissions-table">
              <thead>
                <tr className="border-b border-[#1e3a5f]">
                  {["Company", "Email", "Governance", "Readiness %", "Submission Risk", "FOCI Risk", "Effort", "Cost", "Submitted"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-['Manrope',sans-serif] font-semibold text-[#94a3b8] uppercase tracking-widest whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-[#94a3b8]">
                      <Loader2 className="animate-spin mx-auto" size={20} />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-[#94a3b8] text-sm">
                      No submissions found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((s, i) => {
                    const r = s.results || {};
                    return (
                      <tr
                        key={s.submission_id || i}
                        data-testid={`submission-row-${i}`}
                        className="border-b border-[#1e3a5f] hover:bg-[#0a1f33]/50 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-[#f1f5f9] whitespace-nowrap">{s.company_name || "—"}</td>
                        <td className="px-4 py-3 text-[#94a3b8] whitespace-nowrap">{s.email || "—"}</td>
                        <td className="px-4 py-3 font-mono text-[#f1f5f9]">{r.governance_level || "—"}</td>
                        <td className="px-4 py-3 font-['JetBrains_Mono',monospace] text-[#c9a84c]">
                          {r.readiness_percent != null ? `${r.readiness_percent.toFixed(1)}%` : "—"}
                        </td>
                        <td className="px-4 py-3"><RiskBadge risk={r.submission_risk || "—"} /></td>
                        <td className="px-4 py-3"><RiskBadge risk={r.foci_risk || "—"} /></td>
                        <td className="px-4 py-3 text-[#94a3b8] whitespace-nowrap">{r.effort_estimate || "—"}</td>
                        <td className="px-4 py-3 text-[#94a3b8] whitespace-nowrap">{r.cost_estimate || "—"}</td>
                        <td className="px-4 py-3 font-mono text-[#334155] whitespace-nowrap text-xs">
                          {s.submitted_at ? s.submitted_at.slice(0, 10) : "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
